import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { IRoadLayoutConfig, ILightingClassConfig, MClassEnum, CClassEnum, PClassEnum, CustomClassModalData } from "types";
import { APP_CLASSES_SHOW_LEFT_RIGHT_TEXT } from "consts";
import { CustomClassTable } from "./CustomClassTable";
import ClassOptions from "./ClassOptions";
import getStyles from "./styles";

type Props = {
  border: "leftBorder" | "rightBorder";
  roadLayoutConfig: IRoadLayoutConfig;
  lightingClassConfig: ILightingClassConfig;
  onLightingClassConfig: (classType: string, value: MClassEnum | CClassEnum | PClassEnum | CustomClassModalData) => void;
  onOpenModal: (road: "main" | "rightBorder" | "leftBorder") => void;
};

const BorderClasses: FC<Props> = ({ border, roadLayoutConfig, lightingClassConfig, onLightingClassConfig, onOpenModal }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.down("md"));
  const styles = useMemo(() => getStyles(theme).BorderClasses, [theme]);

  const { borderAreaLeft, borderAreaRight } = roadLayoutConfig;
  const { borderAreaLeftClasses, borderAreaRightClasses, customClasses } = lightingClassConfig;

  const isLeftBorder = border === "leftBorder";
  const selectedClasses = isLeftBorder ? borderAreaLeftClasses : borderAreaRightClasses;
  const customClassData = isLeftBorder ? customClasses?.leftBorderCustomClass : customClasses?.rightBorderCustomClass;

  const sxContainer = useMemo(() => ({
    width: borderAreaLeft && borderAreaRight ? "100%" : "calc(50% - 4 * var(--rhythm))",
    ...styles.container,
  }), [borderAreaLeft, borderAreaRight, styles.container]);

  const handleOpenModal = useCallback(() => {
    onOpenModal?.(border);
  }, [border, onOpenModal]);

  return (
    <Paper sx={sxContainer}>
      <Typography variant="h3" sx={styles.title}>
        {t(isLeftBorder ? "Border Area Left Classes" : "Border Area Right Classes")}
      </Typography>

      {!APP_CLASSES_SHOW_LEFT_RIGHT_TEXT && (
        <Typography variant="h6" sx={styles.subtitle}>
          {t("Categorization of the border area with a factor of 2.5 smaller.")}
        </Typography>
      )}

      <Stack sx={styles.classes}>
        <ClassOptions
          noDesc
          classType="C"
          name={isLeftBorder ? "BorderAreaLeftCClasses" : "BorderAreaRightCClasses"}
          selected={selectedClasses as string}
          onChange={onLightingClassConfig}
        />
        <ClassOptions
          noDesc
          classType="P"
          name={isLeftBorder ? "BorderAreaLeftPClasses" : "BorderAreaRightPClasses"}
          selected={selectedClasses as string}
          onChange={onLightingClassConfig}
        />
      </Stack>

      <CustomClassTable data={customClassData} sx={styles.custom} />

      <Stack component="footer" sx={styles.actions}>
        <Button variant={md ? "text" : "contained"} onClick={handleOpenModal}>
          {t("Custom Class")}
        </Button>
      </Stack>
    </Paper>
  );
};

export default BorderClasses;
